/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */

import {ColorPicker} from '@vtaits/react-color-picker';
import '@vtaits/react-color-picker/index.css';
import React from 'react';
import ReactDOM from 'react-dom';

import PropTypes from 'prop-types';

import MiscUtils from '../../utils/MiscUtils';

import './style/ColorButton.css';

const defaultColors = [
    // [r, g, b, a]
    [255, 255, 255, 1],
    [0, 0, 0, 1],
    [255, 105,   0, 1],
    [252, 185,   0, 1],
    [  0, 208, 132, 1],
    [142, 209, 252, 1],
    [  6, 147, 227, 1],
    [171, 184, 195, 1],
    [235,  20,  76, 1],
    [247, 141, 167, 1]
];


export default class ColorButton extends React.Component {
    static propTypes = {
        alpha: PropTypes.bool,
        color: PropTypes.array,
        defaultColors: PropTypes.array,
        onColorChanged: PropTypes.func
    };
    static defaultProps = {
        alpha: true,
        defaultColors: defaultColors,
        color: [255, 255, 255, 1],
        onColorChanged: (/* color */) => {}
    };
    state = {
        colors: defaultColors,
        pickerPos: null,
        hexStr: null
    };
    constructor(props) {
        super(props);
        this.menuContainer = null;
        this.state.colors = props.defaultColors;
    }
    render() {
        const curColor = this.props.color;
        const pickerStyle = () => ({
            left: `calc(${this.state.pickerPos[0]}px - 6.5em)`,
            top: `calc(${this.state.pickerPos[1]}px + 0.7em)`
        });
        return (
            <div className="ColorButton">
                <div className="colorbutton-icon" onClick={this.togglePicker} onKeyDown={MiscUtils.checkKeyActivate} tabIndex={0}>
                    <span style={{backgroundColor: this.cssColor(curColor)}} />
                </div>
                {this.state.pickerPos ? ReactDOM.createPortal((
                    <div className="colorbutton-picker" style={pickerStyle()}>
                        <div className="colorbutton-picker-icons">
                            {this.state.colors.map((color, idx) => (
                                <div className="colorbutton-icon" key={"color" + idx}
                                    onClick={() => this.selectColor(idx)} onContextMenu={ev => this.replaceDefaultColor(ev, idx)}
                                    onKeyDown={MiscUtils.checkKeyActivate} tabIndex={0}
                                >
                                    <span style={{backgroundColor: this.cssColor(color)}} />
                                </div>
                            ))}
                        </div>
                        <ColorPicker className="colorbutton-picker-spectrum" onDrag={this.changeColor} saturationHeight={150} saturationWidth={null} value={this.cssColorRGB(curColor)} />
                        <div className="colorbutton-picker-input controlgroup">
                            <div className="colorbutton-icon">
                                <span style={{backgroundColor: this.cssColor(curColor)}} />
                            </div>
                            <input onChange={ev => this.changeColor(ev.target.value)} type="text" value={this.state.hexStr || this.hexColor(curColor)} />
                            {this.props.alpha ? (
                                <div className="colorbutton-picker-alpha">
                                    <div>
                                        <input max="1" min="0" onChange={ev => this.changeColorAlpha(ev.target.value)} step="0.1" type="range" value={curColor[3]}/>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </div>
                ), this.menuContainer) : null}
            </div>
        );
    }
    cssColor(color) {
        return "rgba(" + color.join(",") + ")";
    }
    cssColorRGB(color) {
        return "rgb(" + color.slice(0, 3).join(",") + ")";
    }
    hexColor(color) {
        return (0x1000000 + (color[2] | (color[1] << 8) | (color[0] << 16))).toString(16).slice(1).toUpperCase();
    }
    togglePicker = (ev) => {
        if (this.menuContainer) {
            // Should not happen...
            this.menuContainer.parentElement.removeChild(this.menuContainer);
            this.menuContainer = null;
        }
        const container = MiscUtils.appContainer(ev.currentTarget);
        this.menuContainer = ev.view.document.createElement("div");
        this.menuContainer.className = "colorbutton-picker-container";
        container.appendChild(this.menuContainer);
        this.menuContainer.addEventListener('click', (ev2) => {
            if (ev2.currentTarget === ev2.target) {
                this.setState({pickerPos: null, hexStr: null}, () => {
                    this.menuContainer.parentElement.removeChild(this.menuContainer);
                    this.menuContainer = null;
                });
            }
        });
        const rect = ev.currentTarget.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        this.setState({hexStr: null, pickerPos: [rect.left - containerRect.left + 0.5 * rect.width, rect.bottom - containerRect.top]});
        ev.stopPropagation();
    };
    selectColor = (idx) => {
        this.setState({hexStr: null});
        const n = this.props.alpha ? 4 : 3;
        this.props.onColorChanged([...this.state.colors[idx]].slice(0, n));
    };
    replaceDefaultColor = (ev, idx) => {
        this.setState(state => {
            const newColors = [...state.colors];
            newColors[idx] = [...this.props.color];
            return {colors: newColors};
        });
        this.forceUpdate();
        ev.preventDefault();
    };
    changeColor = (hexStr) => {
        const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexStr);
        if (match) {
            const newColor = [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16), this.props.color[3]];
            this.setState({hexStr: null});
            const n = this.props.alpha ? 4 : 3;
            this.props.onColorChanged(newColor.slice(0, n));
        } else {
            this.setState({hexStr: hexStr});
        }
    };
    changeColorAlpha = (alpha) => {
        const newColor = [...this.props.color.slice(0, 3), parseFloat(alpha)];
        this.props.onColorChanged(newColor);
    };
}
